import { UserButton, useAuth } from "@clerk/react";
import { LogInIcon, ServerIcon, SmartphoneIcon } from "lucide-react";

import {
  useManagedAccountTransition,
  type ManagedAccountTransition,
} from "../../cloud/managedAuth";
import { hasCloudPublicConfig, resolveCloudClerkMount } from "../../cloud/publicConfig";
import { Button } from "../ui/button";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "../ui/sidebar";
import { MobileClientsUserProfilePage } from "./MobileClientsUserProfilePage";
import { CleoConnectUserProfilePage } from "./CleoConnectUserProfilePage";
import { useCleoConnectAuthPrompt } from "./useCleoConnectAuthPrompt";

export function CleoConnectSidebarSignIn() {
  // The mount law, never key presence: the configured halves call useAuth,
  // which throws outside a mounted ClerkProvider (DH1).
  if (!resolveCloudClerkMount().mount) return null;

  return <ConfiguredCleoConnectSidebarSignIn />;
}

export function CleoConnectSidebarAvatar() {
  if (!resolveCloudClerkMount().mount) return null;

  return <ConfiguredCleoConnectSidebarAvatar />;
}

function ConfiguredCleoConnectSidebarAvatar() {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded || !isSignedIn) return null;

  // Mobile clients and Cleo Connect list relay environments: relay features, off when the build names no relay (a Dev app
  // from source mounts Clerk for the window's World sign-in only).
  if (!hasCloudPublicConfig()) {
    return (
      <UserButton
        appearance={{
          elements: {
            avatarBox: "size-7",
            userButtonTrigger: "rounded-lg p-1 hover:bg-sidebar-row-hover",
          },
        }}
      />
    );
  }

  return (
    <UserButton
      appearance={{
        elements: {
          avatarBox: "size-7",
          userButtonTrigger: "rounded-lg p-1 hover:bg-sidebar-row-hover",
        },
      }}
    >
      <UserButton.UserProfilePage
        label="Mobile clients"
        labelIcon={<SmartphoneIcon className="size-4" />}
        url="mobile-clients"
      >
        <MobileClientsUserProfilePage />
      </UserButton.UserProfilePage>
      <UserButton.UserProfilePage
        label="Cleo Connect"
        labelIcon={<ServerIcon className="size-4" />}
        url="cleo-connect"
      >
        <CleoConnectUserProfilePage />
      </UserButton.UserProfilePage>
    </UserButton>
  );
}

export function CleoConnectCleanupFailedNotice({
  transition,
}: {
  readonly transition: ManagedAccountTransition;
}) {
  if (transition.phase !== "failed") return null;

  return (
    <div role="alert" className="flex flex-col gap-2 px-2 py-1.5 text-xs">
      <p>
        Cleo couldn't clear the previous account's Cleo Connect connections, so this account isn't
        connected yet.
      </p>
      <div className="flex gap-2">
        <Button size="xs" onClick={transition.retry}>
          Retry
        </Button>
        <Button size="xs" variant="outline" onClick={transition.signOut}>
          Sign out
        </Button>
      </div>
    </div>
  );
}

function ConfiguredCleoConnectSidebarSignIn() {
  const { isLoaded, isSignedIn } = useAuth();
  const { authPrompt, openAuthPrompt } = useCleoConnectAuthPrompt();
  const transition = useManagedAccountTransition();

  if (isLoaded && isSignedIn) return <CleoConnectCleanupFailedNotice transition={transition} />;
  if (!isLoaded) return null;

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton onClick={openAuthPrompt}>
            <LogInIcon />
            <span>Sign in to Cleo Connect</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
      {authPrompt}
    </>
  );
}
